import Phaser from 'phaser';
import type { HitQuality } from '@/utils/timing';
import { THEME } from '@/config/theme';

export class VFXManager {
  private scene: Phaser.Scene;
  private particles: Phaser.GameObjects.Particles.ParticleEmitter | null = null;
  private streakGlow: Phaser.GameObjects.Rectangle | null = null;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.createParticleTexture();
  }

  private createParticleTexture(): void {
    // Create a simple particle texture if it doesn't exist
    if (!this.scene.textures.exists('particle')) {
      const graphics = this.scene.make.graphics({ x: 0, y: 0 });
      graphics.fillStyle(0xffffff, 1);
      graphics.fillCircle(8, 8, 8);
      graphics.generateTexture('particle', 16, 16);
      graphics.destroy();
    }
  }

  /**
   * Show hit effect at position
   */
  showHitEffect(x: number, y: number, quality: HitQuality): void {
    const colors: Record<HitQuality, number> = {
      perfect: THEME.colors.hit.perfectHex,
      good: THEME.colors.hit.goodHex,
      ok: THEME.colors.hit.okHex,
      miss: THEME.colors.hit.missHex,
    };

    const color = colors[quality];
    const count = quality === 'perfect' ? 20 : quality === 'good' ? 12 : 8;

    // Burst of particles
    const emitter = this.scene.add.particles(x, y, 'particle', {
      speed: { min: 100, max: 200 },
      scale: { start: 0.5, end: 0 },
      alpha: { start: 1, end: 0 },
      lifespan: 400,
      tint: color,
      emitting: false,
    });

    emitter.explode(count);

    // Clean up after animation
    this.scene.time.delayedCall(500, () => {
      emitter.destroy();
    });

    // Add expanding ring for perfect hits
    if (quality === 'perfect') {
      this.showPerfectRing(x, y);
    }
  }

  private showPerfectRing(x: number, y: number): void {
    const ring = this.scene.add.circle(x, y, 20, THEME.colors.primaryHex, 0);
    ring.setStrokeStyle(3, THEME.colors.primaryHex, 1);

    this.scene.tweens.add({
      targets: ring,
      radius: 60,
      alpha: 0,
      duration: 300,
      ease: 'Quad.easeOut',
      onUpdate: () => {
        ring.setStrokeStyle(3, THEME.colors.primaryHex, ring.alpha);
      },
      onComplete: () => ring.destroy(),
    });
  }

  /**
   * Show miss effect
   */
  showMissEffect(x: number, y: number): void {
    // Red X mark
    const size = 30;
    const graphics = this.scene.add.graphics();
    graphics.lineStyle(4, THEME.colors.hit.missHex, 1);
    graphics.lineBetween(x - size/2, y - size/2, x + size/2, y + size/2);
    graphics.lineBetween(x + size/2, y - size/2, x - size/2, y + size/2);

    this.scene.tweens.add({
      targets: graphics,
      alpha: 0,
      y: y - 20,
      duration: 500,
      onComplete: () => graphics.destroy(),
    });

    // Screen shake
    this.scene.cameras.main.shake(100, 0.005);
  }

  /**
   * Update streak visuals
   */
  updateStreakEffect(streak: number, hitZoneX: number, trackY: number): void {
    // Remove old glow
    if (this.streakGlow) {
      this.streakGlow.destroy();
      this.streakGlow = null;
    }

    if (streak < 10) return;

    // Create glow based on streak level
    let color: number;
    let intensity: number;
    let pulseSpeed: number;

    if (streak >= 50) {
      color = THEME.colors.magentaHex;
      intensity = 0.4;
      pulseSpeed = 200;
    } else if (streak >= 25) {
      color = THEME.colors.cyanHex;
      intensity = 0.3;
      pulseSpeed = 300;
    } else {
      color = THEME.colors.yellowHex;
      intensity = 0.2;
      pulseSpeed = 400;
    }

    // Background glow around hit zone
    this.streakGlow = this.scene.add.rectangle(
      hitZoneX, trackY, 100, 100, color, intensity
    );
    this.streakGlow.setDepth(-1);

    // Pulsing effect
    this.scene.tweens.add({
      targets: this.streakGlow,
      alpha: intensity * 0.5,
      duration: pulseSpeed,
      yoyo: true,
      repeat: -1,
    });
  }

  /**
   * Show streak milestone celebration
   */
  showStreakMilestone(streak: number, x: number, y: number): void {
    // Firework-like burst
    const colors = [THEME.colors.primaryHex, THEME.colors.cyanHex, THEME.colors.magentaHex, THEME.colors.yellowHex];

    for (let i = 0; i < 3; i++) {
      this.scene.time.delayedCall(i * 100, () => {
        const emitter = this.scene.add.particles(x, y, 'particle', {
          speed: { min: 150, max: 300 },
          scale: { start: 0.6, end: 0 },
          alpha: { start: 1, end: 0 },
          lifespan: 600,
          tint: colors[i % colors.length],
          emitting: false,
        });

        emitter.explode(15);

        this.scene.time.delayedCall(700, () => {
          emitter.destroy();
        });
      });
    }
  }

  /**
   * Show combo break effect
   */
  showComboBreak(): void {
    // Flash screen red briefly
    const flash = this.scene.add.rectangle(
      this.scene.cameras.main.centerX,
      this.scene.cameras.main.centerY,
      this.scene.cameras.main.width,
      this.scene.cameras.main.height,
      THEME.colors.hit.missHex,
      0.2
    );
    flash.setDepth(100);

    this.scene.tweens.add({
      targets: flash,
      alpha: 0,
      duration: 300,
      onComplete: () => flash.destroy(),
    });
  }

  /**
   * Clean up
   */
  destroy(): void {
    if (this.streakGlow) {
      this.streakGlow.destroy();
    }
    if (this.particles) {
      this.particles.destroy();
    }
  }
}
